import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { useTrackShipment } from '../hooks/useShipping';
import { ProofOfDelivery, TrackingEvent } from '../services/shippingService';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Callout } from '../components/ui/Callout';
import { Kicker } from '../components/ui/Kicker';
import { Field } from '../components/ui/Field';
import { Input, InputGroup, Select } from '../components/ui/Input';
import { Icon, type IconName } from '../components/ui/Icon';
import { TrackEventsTimeline } from './track/TrackEventsTimeline';

type StatusInfo = {
  step: number;
  iconName: IconName;
  tone: 'ok' | 'info' | 'warn' | 'err' | 'default';
  label: string;
  isSubStatus: boolean;
  parentStatus?: string;
};

const getStatusInfo = (status: string): StatusInfo => {
  const s = status.toUpperCase();
  if (s.includes('DELIVERED'))
    return { step: 4, iconName: 'checkCircle', tone: 'ok', label: 'Delivered', isSubStatus: false };
  if (s === 'READY_FOR_PICKUP')
    return { step: 3, iconName: 'package', tone: 'info', label: 'Ready for pickup', isSubStatus: true, parentStatus: 'OUT_FOR_DELIVERY' };
  if (s === 'OUT_FOR_DELIVERY')
    return { step: 3, iconName: 'truck', tone: 'info', label: 'Out for delivery', isSubStatus: false };
  if (s === 'IN_TRANSIT')
    return { step: 2, iconName: 'plane', tone: 'info', label: 'In transit', isSubStatus: false };
  if (s === 'PICKED_UP')
    return { step: 1, iconName: 'box', tone: 'warn', label: 'Picked up', isSubStatus: false };
  if (s === 'ORDER_CREATED')
    return { step: 0, iconName: 'package', tone: 'default', label: 'Order created', isSubStatus: false };
  return {
    step: 0,
    iconName: 'clock',
    tone: 'default',
    label: status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase()),
    isSubStatus: false,
  };
};

const PROGRESS_STEPS: { key: string; label: string }[] = [
  { key: 'ORDER_CREATED', label: 'Order created' },
  { key: 'PICKED_UP', label: 'Picked up' },
  { key: 'IN_TRANSIT', label: 'In transit' },
  { key: 'OUT_FOR_DELIVERY', label: 'Out for delivery' },
  { key: 'DELIVERED', label: 'Delivered' },
];

const PROVIDERS = ['', 'fedex', 'dhl', 'ups', 'aramex', 'usps'];

const TrackShipmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { trackingNumber: urlTrackingNumber } = useParams<{ trackingNumber?: string }>();
  const { search } = useLocation();
  const queryParams = React.useMemo(() => new URLSearchParams(search), [search]);
  const queryTrackingNumber = queryParams.get('tracking');
  const queryProvider = queryParams.get('provider');

  const [inputTrackingNumber, setInputTrackingNumber] = useState('');
  const [inputProvider, setInputProvider] = useState('');
  const [currentTrackingNumber, setCurrentTrackingNumber] = useState<string | undefined>(
    urlTrackingNumber || queryTrackingNumber || undefined
  );
  const [currentProvider, setCurrentProvider] = useState<string | undefined>(
    queryProvider || undefined
  );

  const {
    data: rawTrackingData,
    isLoading,
    isError,
    error,
    refetch,
  } = useTrackShipment(currentTrackingNumber || '', currentProvider);

  const trackingData = React.useMemo(() => {
    if (!rawTrackingData) return null;
    const sortedEvents = [...rawTrackingData.events].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    return { ...rawTrackingData, events: sortedEvents };
  }, [rawTrackingData]);

  useEffect(() => {
    const trackingFromUrl = urlTrackingNumber || queryTrackingNumber;
    const providerFromUrl = queryProvider;
    if (trackingFromUrl) {
      setInputTrackingNumber(trackingFromUrl);
      setCurrentTrackingNumber(trackingFromUrl);
    }
    if (providerFromUrl) {
      setInputProvider(providerFromUrl);
      setCurrentProvider(providerFromUrl);
    }
  }, [urlTrackingNumber, queryTrackingNumber, queryProvider]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputTrackingNumber.trim()) {
      setCurrentTrackingNumber(inputTrackingNumber.trim());
      setCurrentProvider(inputProvider.trim() || undefined);
      const sp = new URLSearchParams();
      if (inputProvider.trim()) sp.set('provider', inputProvider.trim());
      const qs = sp.toString();
      navigate(`/track/${inputTrackingNumber.trim()}${qs ? `?${qs}` : ''}`);
    }
  };

  const statusInfo: StatusInfo = trackingData
    ? getStatusInfo(trackingData.status)
    : { step: 0, iconName: 'clock', tone: 'default', label: 'Unknown status', isSubStatus: false };

  const formatDateTime = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      return {
        date: format(date, 'MMM d, yyyy'),
        time: format(date, 'h:mm a'),
        relative: formatDistanceToNow(date, { addSuffix: true }),
      };
    } catch {
      return { date: 'Invalid date', time: '', relative: '' };
    }
  };

  const decodePdfBlob = (proofOfDelivery: ProofOfDelivery): Blob => {
    const byteCharacters = atob(proofOfDelivery.content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
  };

  const handleDownloadPdf = (proofOfDelivery: ProofOfDelivery, trackingNumber: string) => {
    try {
      const blob = decodePdfBlob(proofOfDelivery);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proof-of-delivery-${trackingNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  };

  const handlePreviewPdf = (proofOfDelivery: ProofOfDelivery) => {
    try {
      const blob = decodePdfBlob(proofOfDelivery);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error previewing PDF:', err);
    }
  };

  const groupedEvents = React.useMemo(() => {
    if (!trackingData?.events) return [] as Array<{
      status: string;
      displayStatus: string;
      events: TrackingEvent[];
      subGroups?: Array<{ status: string; events: TrackingEvent[]; statusInfo: StatusInfo }>;
      isLatest: boolean;
      statusInfo: StatusInfo;
    }>;

    const groups: Array<{
      status: string;
      displayStatus: string;
      events: TrackingEvent[];
      subGroups?: Array<{ status: string; events: TrackingEvent[]; statusInfo: StatusInfo }>;
      isLatest: boolean;
      statusInfo: StatusInfo;
    }> = [];

    trackingData.events.forEach((event: TrackingEvent, index: number) => {
      const eventStatusInfo = getStatusInfo(event.status);
      const lastGroup = groups[groups.length - 1];

      if (eventStatusInfo.isSubStatus && eventStatusInfo.parentStatus) {
        const parentStatusInfo = getStatusInfo(eventStatusInfo.parentStatus);
        if (
          lastGroup &&
          (lastGroup.status === eventStatusInfo.parentStatus ||
            lastGroup.displayStatus === parentStatusInfo.label)
        ) {
          if (!lastGroup.subGroups) lastGroup.subGroups = [];
          const lastSubGroup = lastGroup.subGroups[lastGroup.subGroups.length - 1];
          if (lastSubGroup && lastSubGroup.status === event.status) {
            lastSubGroup.events.push(event);
          } else {
            lastGroup.subGroups.push({ status: event.status, events: [event], statusInfo: eventStatusInfo });
          }
        } else {
          groups.push({
            status: eventStatusInfo.parentStatus,
            displayStatus: parentStatusInfo.label,
            events: [],
            subGroups: [{ status: event.status, events: [event], statusInfo: eventStatusInfo }],
            isLatest: index === 0,
            statusInfo: parentStatusInfo,
          });
        }
      } else if (lastGroup && lastGroup.status === event.status) {
        lastGroup.events.push(event);
      } else {
        groups.push({
          status: event.status,
          displayStatus: eventStatusInfo.label,
          events: [event],
          isLatest: index === 0,
          statusInfo: eventStatusInfo,
        });
      }
    });

    return groups;
  }, [trackingData]);

  // ── Empty state (no tracking number entered yet) ─────────────
  if (!currentTrackingNumber && !isLoading) {
    return (
      <Page width="tight">
        <div style={{ paddingTop: 56, textAlign: 'center', marginBottom: 28 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: 'var(--bg-tint)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
            }}
          >
            <Icon name="route" size={24} />
          </div>
          <h1 style={{ fontSize: 32, letterSpacing: '-0.02em' }}>Track a shipment</h1>
          <p className="muted" style={{ marginTop: 8 }}>
            Enter your tracking number to see the latest status and event history.
          </p>
        </div>

        <form onSubmit={handleSearchSubmit}>
          <InputGroup style={{ padding: 4 }}>
            <Input
              value={inputTrackingNumber}
              onChange={(e) => setInputTrackingNumber(e.target.value)}
              placeholder="e.g. AS-482-ZH93-LK7"
              inputSize="lg"
              style={{ border: 'none', fontSize: 16 }}
            />
            <Button type="submit" variant="blue" disabled={!inputTrackingNumber.trim()} style={{ margin: 2 }}>
              <Icon name="search" size={15} />
              <span>Track</span>
            </Button>
          </InputGroup>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Field style={{ flex: 1 }}>
              <Select
                value={inputProvider}
                onChange={(e) => setInputProvider(e.target.value)}
                aria-label="Carrier (optional)"
              >
                {PROVIDERS.map((p) => (
                  <option key={p || 'auto'} value={p}>
                    {p ? `Carrier: ${p.toUpperCase()}` : 'Auto-detect carrier'}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </form>

        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'center' }}>
          <Button size="sm" variant="ghost">
            <Icon name="qr" size={14} />
            <span>Scan QR</span>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setInputTrackingNumber('AS-482-ZH93-LK7');
              setCurrentTrackingNumber('AS-482-ZH93-LK7');
            }}
          >
            Try demo
          </Button>
        </div>
      </Page>
    );
  }

  // ── Loading ───────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Page width="wide">
        <div className="muted" style={{ textAlign: 'center', padding: 64 }}>
          <Icon name="refresh" size={28} />
          <div style={{ marginTop: 12, fontSize: 15 }}>
            Tracking <span className="mono">{currentTrackingNumber}</span>…
          </div>
        </div>
      </Page>
    );
  }

  // ── Error ─────────────────────────────────────────────────────
  if (isError || !trackingData) {
    const message =
      (error as any)?.message ||
      'We could not find any tracking information for this number. Please check the number and carrier and try again.';
    return (
      <Page width="narrow">
        <Callout tone="warn">
          <Icon name="alertCircle" size={16} />
          <div>
            <strong>Tracking not found</strong>
            <div style={{ marginTop: 4 }}>{message}</div>
          </div>
        </Callout>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
          <Button size="sm" onClick={() => refetch()}>
            <Icon name="refresh" size={14} />
            <span>Retry</span>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setCurrentTrackingNumber(undefined);
              setCurrentProvider(undefined);
              navigate('/track');
            }}
          >
            <Icon name="arrowLeft" size={14} />
            <span>Track another</span>
          </Button>
        </div>
      </Page>
    );
  }

  // ── Filled state ──────────────────────────────────────────────
  const latestEvent = trackingData.events[0];
  const latestFormatted = latestEvent ? formatDateTime(latestEvent.timestamp) : null;
  const isDelivered = statusInfo.step === 4;
  const carrier = trackingData.provider || currentProvider || 'Carrier';

  return (
    <Page width="wide">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setCurrentTrackingNumber(undefined);
          setCurrentProvider(undefined);
          navigate('/track');
        }}
        style={{ marginBottom: 16 }}
      >
        <Icon name="arrowLeft" size={14} />
        <span>Track another shipment</span>
      </Button>

      <PageHead>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Kicker>Tracking</Kicker>
            <span className="mono" style={{ fontSize: 14, color: 'var(--ink-2)' }}>
              {trackingData.tracking_number}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => navigator.clipboard.writeText(trackingData.tracking_number)}
              aria-label="Copy tracking number"
            >
              <Icon name="copy" size={12} />
            </button>
          </div>
          <PageTitle style={{ marginTop: 8 }}>
            {isDelivered ? 'Delivered' : statusInfo.label}
            {!isDelivered && latestFormatted?.relative && (
              <span className="muted" style={{ fontSize: 18, fontWeight: 400, marginInlineStart: 8 }}>
                · last update {latestFormatted.relative}
              </span>
            )}
          </PageTitle>
          <PageSub>
            <span style={{ textTransform: 'capitalize' }}>{carrier}</span>
            {trackingData.events.length > 0 && ` · ${trackingData.events.length} events`}
          </PageSub>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button size="sm" onClick={() => refetch()}>
            <Icon name="refresh" size={14} />
            <span>Refresh</span>
          </Button>
          <Button size="sm" title="Notify me on updates">
            <Icon name="bell" size={14} />
            <span>Notify me</span>
          </Button>
          {trackingData.proof_of_delivery && (
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                handleDownloadPdf(trackingData.proof_of_delivery!, trackingData.tracking_number)
              }
            >
              <Icon name="download" size={14} />
              <span>POD</span>
            </Button>
          )}
        </div>
      </PageHead>

      <div
        style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 20 }}
        className="details-grid"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Route + status banner */}
          <Card pad>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: 120 }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', letterSpacing: '0.08em' }}>
                  CURRENT STATUS
                </div>
                <div style={{ fontWeight: 600, fontSize: 16, marginTop: 4 }}>{statusInfo.label}</div>
                {latestEvent?.location && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {latestEvent.location}
                  </div>
                )}
              </div>

              <div
                style={{
                  flex: 1,
                  position: 'relative',
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 240,
                }}
              >
                <div style={{ flex: 1, height: 2, borderTop: '2px dashed var(--line-strong)' }} />
                <div
                  style={{
                    position: 'absolute',
                    left: `${Math.min(95, Math.max(5, (statusInfo.step / 4) * 100))}%`,
                    transform: 'translateX(-50%)',
                    background: isDelivered ? 'var(--ok)' : 'var(--ink)',
                    color: '#fff',
                    borderRadius: 99,
                    padding: '4px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  <Icon name={statusInfo.iconName} size={12} color="#fff" />
                  {statusInfo.label}
                </div>
              </div>

              <div style={{ flex: 1, minWidth: 120, textAlign: 'right' }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', letterSpacing: '0.08em' }}>
                  CARRIER
                </div>
                <div
                  style={{ fontWeight: 600, fontSize: 16, marginTop: 4, textTransform: 'capitalize' }}
                >
                  {carrier}
                </div>
                {latestFormatted && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    Last update {latestFormatted.date} · {latestFormatted.time}
                  </div>
                )}
              </div>
            </div>
          </Card>

          <TrackEventsTimeline
            groupedEvents={groupedEvents as any}
            totalEvents={trackingData.events.length}
            onRefresh={() => refetch()}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Progress checklist */}
          <Card pad>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Progress</div>
            {PROGRESS_STEPS.map((step, i) => {
              const done = i < statusInfo.step || (i === statusInfo.step && isDelivered);
              const active = i === statusInfo.step && !isDelivered;
              return (
                <div
                  key={step.key}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 99,
                      border: '2px solid',
                      borderColor: done
                        ? 'var(--ok)'
                        : active
                          ? 'var(--brand-blue)'
                          : 'var(--line-strong)',
                      background: done ? 'var(--ok)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {done && <Icon name="check" size={10} color="#fff" strokeWidth={3} />}
                    {active && (
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 99,
                          background: 'var(--brand-blue)',
                        }}
                      />
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                      color: done || active ? 'var(--ink)' : 'var(--ink-4)',
                    }}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </Card>

          {/* Carrier info */}
          <Card pad>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Carrier</div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 12,
                background: 'var(--bg-tint)',
                borderRadius: 8,
              }}
            >
              <div
                className="mono"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 8,
                  background: 'var(--brand-navy)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: 'uppercase',
                }}
              >
                {carrier.slice(0, 3).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{ fontWeight: 600, fontSize: 14, textTransform: 'capitalize' }}
                >
                  {carrier}
                </div>
                <div className="muted mono" style={{ fontSize: 12 }}>
                  {trackingData.tracking_number}
                </div>
              </div>
            </div>
          </Card>

          {/* Proof of delivery */}
          {trackingData.proof_of_delivery && (
            <Card pad>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 12,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>Proof of delivery</div>
                <Badge tone="ok">Available</Badge>
              </div>
              <div
                style={{
                  padding: 14,
                  background: 'var(--bg-tint)',
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <Icon name="file" size={28} color="var(--brand-blue)" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>
                    proof-of-delivery-{trackingData.tracking_number}.pdf
                  </div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    Official carrier document
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="sm"
                  block
                  onClick={() => handlePreviewPdf(trackingData.proof_of_delivery!)}
                >
                  <Icon name="eye" size={13} />
                  <span>Preview</span>
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  block
                  onClick={() =>
                    handleDownloadPdf(
                      trackingData.proof_of_delivery!,
                      trackingData.tracking_number
                    )
                  }
                >
                  <Icon name="download" size={13} />
                  <span>Download</span>
                </Button>
              </div>
              <div className="muted" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.5 }}>
                Recipient signature, delivery timestamp and location as recorded by{' '}
                <span style={{ textTransform: 'capitalize' }}>{carrier}</span>.
              </div>
            </Card>
          )}
        </div>
      </div>
    </Page>
  );
};

export default TrackShipmentPage;
